import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Bot, Inbox, IndianRupee, MessageSquare, Pencil, Users } from 'lucide-react';
import { useClientOverview, type PlatformDays } from '@/api/platform';
import { Badge, Button, Card, ErrorState, PageHeader, Segmented, Skeleton } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { Legend, LineChart, SERIES } from '@/features/analytics/charts';
import { Kpi } from '@/features/analytics/Kpi';
import { cn } from '@/lib/cn';
import { formatMoney, formatNumber, formatRelative, formatTokens, humanize } from '@/lib/format';
import { AiProvidersTable, AiUsageBreakdown } from './AiPanels';
import { EditClientModal } from './EditClientModal';
import { ClientAiManager } from './ClientAiManager';
import { PLAN_MONTHLY_NOTE, PLAN_TONE, STATUS_TONE, failureRate } from './meta';

const RANGES: PlatformDays[] = [7, 30, 90];

/** One client, seen from the platform: revenue, workload and how they use AI. */
export function ClientDetailPage() {
  const { clientId } = useParams();
  const { enterWorkspace } = useAuth();
  const [days, setDays] = useState<PlatformDays>(30);
  const [editing, setEditing] = useState(false);
  const q = useClientOverview(clientId, days);

  const back = (
    <Link
      to="/admin"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft className="h-4 w-4" /> Platform overview
    </Link>
  );
  const rangePicker = (
    <Segmented
      label="Date range"
      value={days}
      onChange={setDays}
      options={RANGES.map((d) => ({ value: d, label: `${d} days` }))}
    />
  );

  if (q.isPending) {
    return (
      <div className="mx-auto max-w-7xl" role="status" aria-label="Loading client">
        {back}
        <Skeleton className="mb-6 h-16 w-80 rounded-xl" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="mx-auto max-w-7xl">
        {back}
        <Card>
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </Card>
      </div>
    );
  }

  const c = q.data;
  const w = c.workspace;
  const n = c.counts;
  const aiTotal = c.ai.daily.reduce((s, d) => s + d.ok + d.failed, 0);
  const aiFail = failureRate(n.aiRequests, n.aiFailed);

  return (
    <div className={cn('mx-auto max-w-7xl transition-opacity', q.isPlaceholderData && 'opacity-60')}>
      {back}
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-2">
            Client
            <Badge tone={PLAN_TONE[w.plan]}>{humanize(w.plan)}</Badge>
            <Badge tone={STATUS_TONE[w.status]}>{humanize(w.status)}</Badge>
          </span>
        }
        title={w.name}
        description={
          <>
            <span className="font-mono">{w.slug}</span>
            {w.contactEmail && ` · ${w.contactName ? `${w.contactName}, ` : ''}${w.contactEmail}`} · joined{' '}
            {formatRelative(w.createdAt)}
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {rangePicker}
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
            <Button onClick={() => enterWorkspace(w.id)}>
              Open workspace <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi
          label="Est. monthly revenue"
          value={c.mrr ? formatMoney(c.mrr, c.currency) : '—'}
          hint={PLAN_MONTHLY_NOTE}
          icon={IndianRupee}
          tint="bg-emerald-50 text-emerald-600 ring-emerald-100"
        >
          <p className="text-xs text-slate-500">
            {w.status !== 'active'
              ? 'Suspended — not billed'
              : c.mrr
                ? `${humanize(w.plan)} plan`
                : 'Free trial'}
          </p>
        </Kpi>
        <Kpi
          label="Team members"
          value={formatNumber(n.users)}
          icon={Users}
          tint="bg-brand-50 text-brand-600 ring-brand-100"
        />
        <Kpi
          label="Customers"
          value={formatNumber(n.customers)}
          icon={MessageSquare}
          tint="bg-teal-50 text-teal-600 ring-teal-100"
        />
        <Kpi
          label={`Tickets (${days}d)`}
          value={formatNumber(n.ticketsCreated)}
          icon={Inbox}
          tint="bg-sky-50 text-sky-600 ring-sky-100"
          trend={{ values: c.tickets.map((d) => d.created), color: SERIES[0] }}
        >
          <p className="text-xs text-slate-500">{formatNumber(n.ticketsSolved)} solved</p>
        </Kpi>
        <Kpi
          label="Open tickets"
          value={formatNumber(n.openTickets)}
          icon={Inbox}
          tint="bg-amber-50 text-amber-600 ring-amber-100"
        />
        <Kpi
          label={`AI requests (${days}d)`}
          value={formatNumber(n.aiRequests)}
          icon={Bot}
          tint="bg-indigo-50 text-indigo-600 ring-indigo-100"
          trend={{ values: c.ai.daily.map((d) => d.ok + d.failed), color: SERIES[0] }}
        >
          <p className="text-xs text-slate-500">
            {aiFail == null ? 'No requests yet' : `${aiFail}% failed`} ·{' '}
            {formatTokens(n.aiInputTokens + n.aiOutputTokens)} tokens
          </p>
        </Kpi>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-base font-bold text-slate-900">Ticket volume</h2>
            <Legend labels={['Created', 'Solved']} />
          </div>
          <LineChart
            dates={c.tickets.map((d) => d.date)}
            series={[
              { label: 'Created', values: c.tickets.map((d) => d.created) },
              { label: 'Solved', values: c.tickets.map((d) => d.solved) },
            ]}
          />
        </Card>
        <Card className="p-5">
          <div className="mb-4">
            <h2 className="font-heading text-base font-bold text-slate-900">AI requests per day</h2>
            <p className="mt-0.5 text-xs text-slate-500">{formatNumber(aiTotal)} in this period</p>
          </div>
          <LineChart
            dates={c.ai.daily.map((d) => d.date)}
            series={[{ label: 'AI requests', values: c.ai.daily.map((d) => d.ok + d.failed) }]}
          />
        </Card>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Card className="p-5 xl:col-span-2">
          <h2 className="font-heading mb-1 text-base font-bold text-slate-900">AI services</h2>
          <p className="mb-4 text-xs text-slate-500">
            {c.ai.aiAgents} AI {c.ai.aiAgents === 1 ? 'agent' : 'agents'} · {c.ai.aiWidgets} chat{' '}
            {c.ai.aiWidgets === 1 ? 'widget' : 'widgets'} answered by AI
          </p>
          <h3 className="mb-2 text-sm font-semibold text-slate-900">Usage by provider</h3>
          <AiProvidersTable rows={c.ai.byProvider} showClients={false} />
        </Card>
        <Card className="p-5">
          <h2 className="font-heading mb-4 text-base font-bold text-slate-900">AI usage</h2>
          <AiUsageBreakdown ai={c.ai} />
        </Card>
      </div>

      <ClientAiManager workspaceId={w.id} workspaceName={w.name} />

      {editing && <EditClientModal client={w} onClose={() => setEditing(false)} />}
    </div>
  );
}
